/**
 * Building an internal link that stays inside the reader's locale.
 *
 * Measured on 2026-07-28 across all 970 localized URLs in the sitemaps: 950 of
 * them contained ZERO links into their own locale. Every generator emitted bare
 * EN paths — `/prices`, `/countries/esim-spain` — and the SPA only rewrote them
 * at click time, in JS. So for a crawler the entire localized tree had no
 * inbound or outbound internal links at all: 950 pages reachable only from the
 * sitemap, with no internal weight. That is the mechanism behind the ~146
 * "Просканирована — не проиндексирована" pages that have not moved for weeks.
 *
 * The rule is not "prefix everything": /ai-agents, /mcp-server, /agents-earn,
 * /data-price-index, /glossary and /regions/* are EN-only, and the server 301s
 * their locale-prefixed forms. Prefixing those would point every localized page
 * at a redirect — trading one crawl problem for another. So localizability is
 * decided by the same allowlist the 404 handler uses (backend known-paths.ts
 * reads LOCALIZED_PATHS from here), and the two cannot drift.
 *
 * Moved to @roamzy/shared on 2026-09-30: the country page renders on both the
 * server and the client, and the client printed bare EN paths.
 */
/**
 * Public routes that exist in every locale. Mirrors the localizable entries of
 * `Router.init([...])` in frontend/src/main.ts.
 */
export const LOCALIZED_PATHS = [
    '/',
    '/prices',
    '/guides',
    '/guides/ios',
    '/guides/android',
    '/faq',
    '/blog',
    '/about',
    '/legal/terms',
    '/legal/privacy',
];
const localizedSet = new Set(LOCALIZED_PATHS);
/** Slug namespaces that exist per-locale (validated elsewhere). */
const LOCALIZED_NAMESPACE_RE = /^\/(?:blog|countries)\/[^/]+$/;
/** Strip a leading locale prefix, yielding the canonical EN path. */
export function stripLocale(path) {
    const clean = (path.split('?')[0] ?? '').split('#')[0] ?? '';
    const stripped = clean.replace(/^\/(?:ru|es|pt|tr|zh)(?=\/|$)/, '');
    return stripped || '/';
}
/** Does this path have a per-locale variant, or is it EN-only? */
export function isLocalizablePath(path) {
    const clean = (path.split('?')[0] ?? '').split('#')[0] ?? '';
    return localizedSet.has(clean) || LOCALIZED_NAMESPACE_RE.test(clean);
}
/**
 * Prefix `path` with the locale when — and only when — a locale variant exists.
 * `path` must be the canonical EN path (leading slash, no locale prefix).
 */
export function localeHref(locale, path) {
    if (locale === 'en')
        return path;
    if (!isLocalizablePath(path))
        return path;
    return path === '/' ? `/${locale}` : `/${locale}${path}`;
}
/** `<a ... href="/path">` — internal, root-relative, not protocol-relative. */
const ANCHOR_HREF_RE = /(<a\b[^>]*\bhref=")(\/(?!\/)[^"#?]*)((?:[?#][^"]*)?")/gi;
/**
 * Localize the internal links inside a rendered article body.
 *
 * The editorial cross-links live in the catalogs as canonical EN paths — 6,278
 * of them across the four localized article and blog catalogs — so every
 * localized article body linked its reader back out into the English tree. The
 * catalogs stay canonical; the locale is applied at render time, which is also
 * what keeps a newly authored article from having to know about locales.
 * Audit 2026-07-28.
 */
export function localizeBodyHrefs(html, locale, opts = {}) {
    if (locale === 'en')
        return html;
    return html.replace(ANCHOR_HREF_RE, (_m, open, path, tail) => {
        const blog = /^\/blog\/([^/]+)$/.exec(path);
        if (blog?.[1] && opts.hasBlogTranslation && !opts.hasBlogTranslation(blog[1])) {
            return `${open}${path}${tail}`;
        }
        return `${open}${localeHref(locale, path)}${tail}`;
    });
}
